import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import StripeConnectBanner from "../components/StripeConnectBanner";
import PartnerPaymentHistory from "../components/PartnerPaymentHistory";
import PortalDocumentHub from "../components/PortalDocumentHub";
import DueDiligenceManager from "../components/DueDiligenceManager";
import LoanCalculator from "../components/LoanCalculator";
import AIUnderwritingReport from "../components/AIUnderwritingReport";
import {
  PortalSidebar,
  PortalEmptyState,
  QuickDeployAction,
  ViewHeader,
} from "../components/PortalShared";
import ExchangeWidget from "../components/ExchangeWidget";
import {
  Building2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  MessageSquare,
  Star,
  CheckCircle,
  Clock,
  MapPin,
  Briefcase,
  CreditCard,
  ArrowRight,
  Eye,
  Send,
  Filter,
  RefreshCw,
  AlertTriangle,
  Home,
  Percent,
  Target,
  Shield,
  Activity,
  ChevronRight,
  Settings,
  FileText,
  FileSignature,
  CalendarDays
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lender Pipeline Stages
const FUNDING_STAGES = [
  { key: 'new', label: 'NEW REQUESTS', color: '#3B82F6' },
  { key: 'reviewing', label: 'REVIEWING', color: '#6366F1' },
  { key: 'interested', label: 'INTERESTED', color: '#8B5CF6' },
  { key: 'negotiating', label: 'NEGOTIATING', color: '#D4AF37' },
  { key: 'funded', label: 'FUNDED', color: '#10B981' },
];

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// System Status Badge
function SystemStatusBadge({ status }) {
  const configs = {
    stable: { text: 'OPTIMAL', bgClass: 'bg-emerald-500/[0.08]', borderClass: 'border-emerald-500/20', textClass: 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' },
    alert: { text: 'ATTENTION', bgClass: 'bg-amber-500/[0.08]', borderClass: 'border-amber-500/20', textClass: 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' },
    bottleneck: { text: 'HIGH DEMAND', bgClass: 'bg-purple-500/[0.08]', borderClass: 'border-purple-500/20', textClass: 'text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]' }
  };
  const config = configs[status] || configs.stable;
  
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 ${config.bgClass} border ${config.borderClass} rounded`}>
      <Shield className={`w-3 h-3 ${config.textClass}`} />
      <span className={`text-[9px] font-semibold ${config.textClass} uppercase tracking-wider`}>{config.text}</span>
    </div>
  );
}

// Global Metric Display
function GlobalMetric({ label, value, trend, positive }) {
  return (
    <div className="text-center">
      <p className="text-[9px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-center gap-2 justify-center">
        <span className="text-xl font-semibold text-white tracking-tight">{value}</span>
        {trend && (
          <span className={`text-[10px] font-medium ${positive ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
            {trend}
          </span>
        )}
      </div>
    </div>
  );
}

// Execution Flow Stage
function ExecutionStage({ stage, count, volume, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative flex-1 p-4 border-t-2 transition-all ${
        isActive 
          ? 'bg-white/[0.03]' 
          : 'hover:bg-white/[0.02] border-t-transparent'
      }`}
      style={{ borderTopColor: isActive ? stage.color : 'transparent' }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: stage.color }}>
          {stage.label}
        </span>
      </div>
      <div className="text-2xl font-bold text-white mb-1">{count}</div>
      <div className="text-[10px] text-white/40">
        {formatCurrency(volume)} capital
      </div>
    </button>
  );
}

// Funding Request Row
function FundingRequestRow({ request, onClick, onExpressInterest }) {
  return (
    <div className="flex items-center gap-4 p-4 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] hover:border-purple-500/30 transition-all">
      {/* Stage Indicator */}
      <div className="w-1 h-12 rounded-full bg-purple-500" />
      
      {/* Property Info */}
      <div className="flex-1">
        <p className="text-white font-medium text-sm">{request.property_address || 'Property Address'}</p>
        <p className="text-white/40 text-xs mt-0.5">
          {request.loan_type || 'Bridge'} • {request.property_type || 'SFH'}
        </p>
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-6">
        <div className="text-right">
          <p className="text-white font-mono text-sm">{formatCurrency(request.loan_amount)}</p>
          <p className="text-[10px] text-white/40">Loan Amount</p>
        </div>
        <div className="text-right">
          <p className="text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] font-mono text-sm">{request.ltv || '--'}%</p>
          <p className="text-[10px] text-white/40">LTV</p>
        </div>
        <div className="text-right">
          <p className="text-white/70 font-mono text-sm">{request.term_months || '--'}mo</p>
          <p className="text-[10px] text-white/40">Term</p>
        </div>
        <Button 
          size="sm"
          onClick={(e) => { e.stopPropagation(); onExpressInterest(request); }}
          className="bg-purple-500/20 hover:bg-purple-500/30 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border border-purple-500/30"
        >
          Express Interest
        </Button>
        <button onClick={onClick}>
          <ChevronRight className="w-4 h-4 text-white/30" />
        </button>
      </div>
    </div>
  );
}


function SettingsProfileView({ profile, fields, onSave }) {
  const [form, setForm] = useState(profile || {});
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try { if (onSave) await onSave(form); toast.success("Profile updated"); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <ViewHeader title="Configuration" subtitle="Manage your portal profile and settings" />
      <div className="max-w-lg bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 space-y-4">
        {fields.map(({ key, label, type = 'text' }) => (
          <div key={key}>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">{label}</label>
            {type === 'textarea' ? (
              <textarea value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} rows={3} className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-[#D4AF37]/50" />
            ) : (
              <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
            )}
          </div>
        ))}
        <button onClick={handleSave} disabled={saving} className="w-full py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold text-sm rounded transition-all disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Lender View Components ────────────────────────────────────────────────────

function LenderRequestsView({ requests, onExpressInterest }) {
  return (
    <div>
      <ViewHeader title="Funding Requests" subtitle="Active loan requests from investors looking for capital" count={requests.length} />
      {requests.length === 0 ? (
        <PortalEmptyState icon={FileText} title="No Active Requests" desc="New funding requests from investors will appear here" />
      ) : (
        <div className="space-y-3">
          {requests.map((req, idx) => (
            <div key={req.request_id || idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 hover:border-purple-500/30 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium text-sm">{req.property_address || 'Investment Property'}</h3>
                    <span className="text-[10px] px-2 py-0.5 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded uppercase">{req.loan_type || 'Bridge'}</span>
                  </div>
                  <p className="text-white/40 text-xs">{req.property_type || 'SFH'} • {req.loan_purpose || 'Acquisition/Rehab'}</p>
                  <div className="flex items-center gap-4 mt-2 text-[10px] text-white/30">
                    <span>LTV: {req.ltv || '--'}%</span>
                    <span>Term: {req.term_months || '--'} mo</span>
                    <span>{req.created_at ? new Date(req.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-white font-bold text-lg">{formatCurrency(req.loan_amount || 0)}</p>
                  <p className="text-white/30 text-[10px]">Requested</p>
                  <button
                    onClick={() => onExpressInterest(req)}
                    className="mt-2 text-xs px-4 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 text-purple-400 font-medium rounded transition-all"
                    data-testid={`express-interest-${idx}`}
                  >
                    Express Interest
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LenderActiveLoansView({ loans }) {
  return (
    <div>
      <ViewHeader title="Active Loans" subtitle="Currently funded deals and their repayment status" count={loans.length} />
      {loans.length === 0 ? (
        <PortalEmptyState icon={DollarSign} title="No Active Loans" desc="Funded deals will appear here once capital is deployed" />
      ) : (
        <div className="space-y-3">
          {loans.map((loan, idx) => {
            const ltv = loan.ltv || 65;
            return (
              <div key={loan.loan_id || idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-white font-medium text-sm">{loan.property_address || 'Active Loan'}</p>
                    <p className="text-white/40 text-xs mt-0.5">{loan.loan_type || 'Bridge Loan'} • {loan.borrower_name || 'Borrower'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold">{formatCurrency(loan.loan_amount || 0)}</p>
                    <span className="text-[10px] px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-[#00FF88] rounded">Active</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center text-xs">
                  <div className="bg-white/[0.02] rounded p-2">
                    <p className="text-white font-medium">{loan.interest_rate || '--'}%</p>
                    <p className="text-white/30 text-[10px]">Rate</p>
                  </div>
                  <div className="bg-white/[0.02] rounded p-2">
                    <p className="text-white font-medium">{ltv}%</p>
                    <p className="text-white/30 text-[10px]">LTV</p>
                  </div>
                  <div className="bg-white/[0.02] rounded p-2">
                    <p className="text-white font-medium">{loan.term_months || '--'} mo</p>
                    <p className="text-white/30 text-[10px]">Term</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LenderDealsView({ interests }) {
  const statusColor = { expressed: 'text-blue-400 bg-blue-500/10 border-blue-500/20', accepted: 'text-[#00FF88] bg-emerald-500/10 border-emerald-500/20', rejected: 'text-[#FF3B30] bg-red-500/10 border-red-500/20' };
  return (
    <div>
      <ViewHeader title="Deal Flow" subtitle="Your expressed interests and deal pipeline" count={interests.length} />
      {interests.length === 0 ? (
        <PortalEmptyState icon={Eye} title="No Deal Flow" desc="Express interest in funding requests to populate your pipeline" />
      ) : (
        <div className="space-y-3">
          {interests.map((deal, idx) => (
            <div key={deal.interest_id || idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 flex items-center justify-between">
              <div>
                <p className="text-white font-medium text-sm">{deal.property_address || 'Deal Pipeline'}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                  <span>{formatCurrency(deal.loan_amount || 0)}</span>
                  <span>{deal.estimated_rate || '--'}% rate offered</span>
                  <span>{deal.created_at ? new Date(deal.created_at).toLocaleDateString() : ''}</span>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 border rounded uppercase font-semibold ${statusColor[deal.status] || statusColor.expressed}`}>
                {deal.status || 'expressed'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LenderMessagesView() {
  return (
    <div>
      <ViewHeader title="Messages" subtitle="Communication with borrowers and deal principals" />
      <PortalEmptyState icon={MessageSquare} title="No Messages Yet" desc="Conversations with borrowers will appear here once deals are in progress" />
    </div>
  );
}

function LenderSavedView() {
  return (
    <div>
      <ViewHeader title="Saved Deals" subtitle="Deals you've bookmarked for future consideration" />
      <PortalEmptyState icon={Star} title="No Saved Deals" desc="Save funding requests from the main pipeline to review later" />
    </div>
  );
}

function LenderRevenueView({ loans, interests }) {
  const totalDeployed = loans.reduce((s, l) => s + (l.loan_amount || 0), 0);
  const estimatedReturn = totalDeployed * 0.10;
  return (
    <div>
      <ViewHeader title="Revenue Stream" subtitle="Capital deployed and projected returns" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Capital Deployed', value: formatCurrency(totalDeployed), color: 'text-purple-400', border: 'border-purple-500/20' },
          { label: 'Active Loans', value: loans.length, color: 'text-[#D4AF37]', border: 'border-[#D4AF37]/20' },
          { label: 'Est. Annual Return', value: formatCurrency(estimatedReturn), color: 'text-[#00FF88]', border: 'border-emerald-500/20' },
        ].map(({ label, value, color, border }) => (
          <div key={label} className={`bg-[#0A0A0A] border ${border} rounded-xl p-5 text-center`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-white/40 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
      <PartnerPaymentHistory partnerType="lender" apiPrefix="/api/lender" paymentTypeLabel="Loan Returns" />
    </div>
  );
}

function LenderAnalyticsView({ dashboard }) {
  const requests = dashboard?.funding_requests || [];
  const interests = dashboard?.my_interests || [];
  const loans = dashboard?.active_loans || [];
  const responseRate = requests.length > 0 ? Math.round((interests.length / requests.length) * 100) : 0;
  return (
    <div>
      <ViewHeader title="Performance Analytics" subtitle="Lending activity and portfolio metrics" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Requests Available', value: requests.length, icon: FileText, color: 'text-blue-400' },
          { label: 'Interests Expressed', value: interests.length, icon: TrendingUp, color: 'text-purple-400' },
          { label: 'Active Loans', value: loans.length, icon: DollarSign, color: 'text-[#00FF88]' },
          { label: 'Response Rate', value: `${responseRate}%`, icon: Target, color: 'text-[#D4AF37]' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 text-center">
            <Icon className={`w-6 h-6 mx-auto mb-2 ${color}`} />
            <p className="text-2xl font-bold text-white">{value}</p>
            <p className="text-white/30 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Lender Nav Sections ────────────────────────────────────────────────────────
const LENDER_NAV = [
  {
    title: "Operations",
    items: [
      { icon: Target, label: "Command Center", view: "dashboard" },
      { icon: FileText, label: "Funding Requests", view: "requests" },
      { icon: Briefcase, label: "Active Loans", view: "active" },
    ]
  },
  {
    title: "Pipeline",
    items: [
      { icon: Eye, label: "Deal Flow", view: "deals" },
      { icon: MessageSquare, label: "Messages", view: "messages" },
      { icon: Star, label: "Saved Deals", view: "saved" },
      { icon: CalendarDays, label: "Deal Calendar", url: "/calendar?from=lender" },
    ]
  },
  {
    title: "Tools",
    items: [
      { icon: CheckCircle, label: "Due Diligence", view: "due-diligence" },
      { icon: DollarSign, label: "Loan Calculator", view: "loan-calculator" },
      { icon: Activity, label: "AI Underwriting", view: "ai-underwriting" },
    ]
  },
  {
    title: "Intelligence",
    items: [
      { icon: DollarSign, label: "Revenue Stream", view: "revenue" },
      { icon: TrendingUp, label: "Performance", view: "analytics" },
      { icon: FileSignature, label: "Documents", view: "documents" },
      { icon: Settings, label: "Configuration", view: "settings" },
    ]
  }
];

export default function LenderCommandCenter({ user, loading: authLoading }) {
  const navigate = useNavigate();
  const [lender, setLender] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState('stable');
  const [isAdminViewing, setIsAdminViewing] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [notifyCounts, setNotifyCounts] = useState({});
  const [dueDiligenceData, setDueDiligenceData] = useState([]);
  const canvasRef = useRef(null);
  
  // Bid submission state
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [bidForm, setBidForm] = useState({
    estimated_rate: "",
    estimated_points: "",
    estimated_term_months: "",
    notes: ""
  });
  const [submittingBid, setSubmittingBid] = useState(false);

  // Handle express interest / submit bid
  const handleExpressInterest = (request) => {
    setSelectedRequest(request);
    setBidForm({
      estimated_rate: "",
      estimated_points: "2",
      estimated_term_months: "12",
      notes: ""
    });
    setShowBidModal(true);
  };

  const handleSubmitBid = async () => {
    if (!selectedRequest || !bidForm.estimated_rate) {
      toast.error("Please enter an estimated rate");
      return;
    }
    
    setSubmittingBid(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/lender/express-interest`,
        {
          funding_request_id: selectedRequest.request_id,
          estimated_rate: bidForm.estimated_rate,
          estimated_points: parseFloat(bidForm.estimated_points) || 0,
          proposed_terms: `${bidForm.estimated_term_months} months`,
          notes: bidForm.notes
        },
        { withCredentials: true }
      );
      
      toast.success("Interest submitted! Operator will be notified.");
      setShowBidModal(false);
      
      // Refresh dashboard
      const dashboardRes = await axios.get(`${BACKEND_URL}/api/lender/dashboard`, { withCredentials: true });
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit interest");
    } finally {
      setSubmittingBid(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Wait for auth check to complete before making decisions
    if (authLoading) {
      return; // Still checking auth, wait
    }
    
    // Always fetch real data — admin users with linked lender accounts should see real data
    // Only show preview mode if there's no session at all
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const [profileRes, dashboardRes, ddRes, notifyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/lender/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/lender/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/portal-tools/due-diligence`, { withCredentials: true }).catch(() => ({ data: { checklists: [] } })),
        axios.get(`${BACKEND_URL}/api/portal-tools/notification-counts`, { withCredentials: true }).catch(() => ({ data: { counts: {} } })),
      ]);
      
      setLender(profileRes.data);
      setDashboard(dashboardRes.data);
      setIsAdminViewing(false);
      setDueDiligenceData(ddRes.data?.checklists || []);
      setNotifyCounts(notifyRes.data?.counts || {});
      
      const newRequests = dashboardRes.data?.funding_requests?.length || 0;
      if (newRequests > 10) setSystemStatus('bottleneck');
      else if (newRequests > 5) setSystemStatus('alert');
      else setSystemStatus('stable');
      
    } catch (err) {
      if (user?.is_admin) {
        setIsAdminViewing(true);
        setLender({ name: 'Admin Preview', email: user.email, company_name: 'Admin View Mode' });
        setDashboard({ funding_requests: [], my_interests: [], active_loans: [], stats: { available: 0, funded: 0 } });
      } else {
        navigate("/lender/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/lender/logout`, {}, { withCredentials: true });
    } catch (err) {}
    navigate("/lender/login");
  };

  // Calculate stage metrics
  const getStageMetrics = () => {
    const requests = dashboard?.funding_requests || [];
    const myInterests = dashboard?.my_interests || [];
    const activeLoans = dashboard?.active_loans || [];
    
    return FUNDING_STAGES.map(stage => {
      let count = 0;
      let volume = 0;
      
      if (stage.key === 'new') {
        const newRequests = requests.filter(r => !myInterests.some(i => i.funding_request_id === r.request_id));
        count = newRequests.length;
        volume = newRequests.reduce((sum, r) => sum + (r.loan_amount || 0), 0);
      } else if (stage.key === 'interested') {
        const interestedRequests = myInterests.filter(i => i.status === 'pending');
        count = interestedRequests.length;
        volume = interestedRequests.reduce((sum, i) => sum + (i.loan_amount || 0), 0);
      } else if (stage.key === 'funded') {
        count = activeLoans.length;
        volume = activeLoans.reduce((sum, l) => sum + (l.loan_amount || 0), 0);
      }
      
      return { ...stage, count, volume };
    });
  };

  // Global metrics
  const globalMetrics = {
    newRequests: dashboard?.funding_requests?.length || 0,
    activeInterests: dashboard?.my_interests?.filter(i => i.status === 'pending').length || 0,
    totalCapitalDeployed: dashboard?.active_loans?.reduce((sum, l) => sum + (l.loan_amount || 0), 0) || 0,
    avgYield: dashboard?.stats?.avg_yield || '--'
  };

  const stageMetrics = getStageMetrics();
  const fundingRequests = dashboard?.funding_requests || [];

  // Canvas animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    let particles = [];
    let animationId;

    const createParticle = () => {
      const stageIdx = Math.floor(Math.random() * (FUNDING_STAGES.length - 1));
      return {
        x: (stageIdx + 0.5) * (canvas.width / FUNDING_STAGES.length),
        y: canvas.height / 2 + (Math.random() - 0.5) * 30,
        targetX: (stageIdx + 1.5) * (canvas.width / FUNDING_STAGES.length),
        progress: 0,
        speed: 0.003 + Math.random() * 0.003,
        color: FUNDING_STAGES[stageIdx].color
      };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      FUNDING_STAGES.forEach((stage, idx) => {
        if (idx < FUNDING_STAGES.length - 1) {
          const x1 = (idx + 0.5) * (canvas.width / FUNDING_STAGES.length);
          const x2 = (idx + 1.5) * (canvas.width / FUNDING_STAGES.length);
          ctx.beginPath();
          ctx.moveTo(x1, canvas.height / 2);
          ctx.lineTo(x2, canvas.height / 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.02)';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });

      if (Math.random() < 0.02 && particles.length < 12) {
        particles.push(createParticle());
      }

      particles = particles.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;
        
        const x = p.x + (p.targetX - p.x) * p.progress;
        const y = p.y + Math.sin(p.progress * Math.PI) * 8;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.4 * (1 - p.progress);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [dashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <PortalSidebar
        navSections={LENDER_NAV}
        activeView={activeView}
        setActiveView={setActiveView}
        homeLink="/lender/portal"
        portal="Lender"
        entity={lender}
        entityInitial="L"
        entitySubtitle={lender?.lender_type || 'Private Lender'}
        accent="purple"
        onLogout={handleLogout}
        notifyCounts={notifyCounts}
        currentRole="lender"
        userRoles={user?.extra_roles || []}
        isAdmin={user?.is_admin || false}
      />
      
      <main className="ml-16 lg:ml-64 transition-all">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">
                    Lender <span className="text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]">Command</span>
                  </h1>
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.15em]">Capital Deployment</p>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                    <div className="relative w-2 h-2">
                      <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                      <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                    </div>
                    <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                  </div>
                  <SystemStatusBadge status={systemStatus} />
                </div>
              </div>

              <div className="flex items-center gap-8">
                <GlobalMetric label="New Requests" value={globalMetrics.newRequests} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric label="Active Interests" value={globalMetrics.activeInterests} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric label="Capital Deployed" value={formatCurrency(globalMetrics.totalCapitalDeployed)} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric label="Avg Yield" value={`${globalMetrics.avgYield}%`} />
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-white/50 font-mono">
                    {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  <p className="text-lg font-mono font-bold text-white">
                    {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <Button 
                  onClick={() => fetchData()}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-semibold h-10 px-5"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Execution Flow Visualization */}
        <section className="border-b border-white/[0.06] relative">
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
          <div className="relative z-10 flex">
            {stageMetrics.map((stage) => (
              <ExecutionStage
                key={stage.key}
                stage={stage}
                count={stage.count}
                volume={stage.volume}
                isActive={activeStage === stage.key}
                onClick={() => setActiveStage(activeStage === stage.key ? null : stage.key)}
              />
            ))}
          </div>
        </section>

        {/* Stripe Connect Banner */}
        <div className="px-6 pt-4">
          <StripeConnectBanner 
            partnerType="lender" 
            apiPrefix="/api/lender"
            compact={true}
          />
        </div>

        {/* Main Content Grid */}
        <div className="p-6">
          {(() => {
            switch(activeView) {
              case 'requests': return <LenderRequestsView requests={fundingRequests} onExpressInterest={handleExpressInterest} />;
              case 'active': return <LenderActiveLoansView loans={dashboard?.active_loans || []} />;
              case 'deals': return <LenderDealsView interests={dashboard?.my_interests || []} />;
              case 'messages': return <LenderMessagesView />;
              case 'saved': return <LenderSavedView />;
              case 'revenue': return <LenderRevenueView loans={dashboard?.active_loans || []} interests={dashboard?.my_interests || []} />;
              case 'analytics': return <LenderAnalyticsView dashboard={dashboard} />;
              case 'due-diligence': return <DueDiligenceManager checklists={dueDiligenceData} />;
              case 'loan-calculator': return <LoanCalculator />;
              case 'ai-underwriting': return <AIUnderwritingReport />;
              case 'documents': return <PortalDocumentHub portalType="lender" />;
              case 'settings': return (
                <SettingsProfileView
                  profile={lender}
                  fields={[
                    { key: 'company_name', label: 'Company Name' },
                    { key: 'name', label: 'Contact Name' },
                    { key: 'email', label: 'Email', type: 'email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'license_number', label: 'License Number' },
                    { key: 'lending_criteria', label: 'Lending Criteria', type: 'textarea' },
                  ]}
                />
              );
              default: return (
                <>
                  {/* Exchange Opportunities Widget — top priority */}
                  <ExchangeWidget role="lender" user={user} className="mb-6" />

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Funding Requests</h2>
                        {activeStage && <button onClick={() => setActiveStage(null)} className="text-[10px] text-white/40 hover:text-white">Clear Filter</button>}
                      </div>
                      <div className="space-y-2">
                        {fundingRequests.length > 0 ? (
                          fundingRequests.slice(0, 5).map((request) => (
                            <FundingRequestRow key={request.request_id} request={request} onClick={() => {}} onExpressInterest={handleExpressInterest} />
                          ))
                        ) : (
                          <div className="text-center py-16 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06]">
                            <FileText className="w-12 h-12 text-white/30 mx-auto mb-3" />
                            <p className="text-white/40">No funding requests available</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Quick Deploy</h2>
                      <div className="space-y-2">
                        <QuickDeployAction icon={Eye} label="Browse Requests" description="View all funding opportunities" variant="primary" onClick={() => setActiveView('requests')} />
                        <QuickDeployAction icon={Briefcase} label="Active Loans" description="Manage current portfolio" onClick={() => setActiveView('active')} />
                        <QuickDeployAction icon={MessageSquare} label="Messages" description="Communication center" onClick={() => setActiveView('messages')} />
                        <QuickDeployAction icon={TrendingUp} label="Analytics" description="Performance metrics" onClick={() => setActiveView('analytics')} />
                      </div>
                      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] p-4 mt-4">
                        <h3 className="text-[10px] font-semibold text-white/30 uppercase tracking-wider mb-3">Portfolio Summary</h3>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Active Loans</span>
                            <span className="text-sm font-mono text-[#BF5AF2]">{dashboard?.active_loans?.length || 0}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Total Deployed</span>
                            <span className="text-sm font-mono font-bold text-white">{formatCurrency(globalMetrics.totalCapitalDeployed)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Pending Interest</span>
                            <span className="text-sm font-mono font-bold text-white">{globalMetrics.activeInterests}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8">
                    <PartnerPaymentHistory partnerType="lender" apiPrefix="/api/lender" paymentTypeLabel="Loan Returns" />
                  </div>
                </>
              );
            }
          })()}
        </div>
      </main>

      {/* Bid Submission Modal */}
      <Dialog open={showBidModal} onOpenChange={setShowBidModal}>
        <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#BF5AF2]" />
              Submit Bid
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded">
                <p className="text-white font-medium">{selectedRequest.property_address}</p>
                <p className="text-white/50 text-sm">
                  Requesting: {formatCurrency(selectedRequest.loan_amount_requested || selectedRequest.loan_amount)} • 
                  {selectedRequest.loan_type?.replace('_', ' ')}
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Interest Rate (%)</Label>
                  <Input 
                    type="number"
                    step="0.25"
                    value={bidForm.estimated_rate} 
                    onChange={(e) => setBidForm({...bidForm, estimated_rate: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white font-mono" 
                    placeholder="12.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Points</Label>
                  <Input 
                    type="number"
                    step="0.5"
                    value={bidForm.estimated_points} 
                    onChange={(e) => setBidForm({...bidForm, estimated_points: e.target.value})} 
                    className="bg-black border-white/[0.08] text-white font-mono" 
                    placeholder="2"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Term (months)</Label>
                <Select 
                  value={bidForm.estimated_term_months} 
                  onValueChange={(v) => setBidForm({...bidForm, estimated_term_months: v})}
                >
                  <SelectTrigger className="bg-black border-white/[0.08] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    <SelectItem value="6" className="text-white hover:bg-white/[0.06]">6 months</SelectItem>
                    <SelectItem value="9" className="text-white hover:bg-white/[0.06]">9 months</SelectItem>
                    <SelectItem value="12" className="text-white hover:bg-white/[0.06]">12 months</SelectItem>
                    <SelectItem value="18" className="text-white hover:bg-white/[0.06]">18 months</SelectItem>
                    <SelectItem value="24" className="text-white hover:bg-white/[0.06]">24 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Notes (optional)</Label>
                <Textarea 
                  value={bidForm.notes} 
                  onChange={(e) => setBidForm({...bidForm, notes: e.target.value})} 
                  className="bg-black border-white/[0.08] text-white min-h-[60px]"
                  placeholder="Additional terms, requirements, or conditions..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBidModal(false)} 
                  className="border-white/[0.1] text-white/70 rounded-none"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitBid}
                  className="bg-[#BF5AF2] hover:bg-[#BF5AF2]/90 text-white font-bold rounded-none"
                  disabled={submittingBid}
                >
                  {submittingBid ? "Submitting..." : "Submit Bid"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
